/**
 * A-021: Goal computed fields
 * Pure functions that compute derived data from a Goal.
 */
import { Goal, GoalType } from '../../types';
import { differenceInDays, parseISO, isValid } from 'date-fns';

export interface GoalComputedFields {
  progressPercentage: number;
  daysRemaining: number;
  onTrack: boolean;
  confidenceScore: number; // 0-100
}

/**
 * Compute progress_percentage from milestones.
 */
export function computeProgressPercentage(goal: Goal): number {
  if (goal.milestones.length === 0) return goal.progress;
  const completed = goal.milestones.filter((m) => m.completed).length;
  return Math.round((completed / goal.milestones.length) * 100);
}

/**
 * Compute days_remaining from target date.
 */
export function computeDaysRemaining(goal: Goal): number {
  const target = parseISO(goal.targetDate);
  if (!isValid(target)) return 0;
  return Math.max(0, differenceInDays(target, new Date()));
}

/**
 * Compute whether a goal is on track based on progress vs expected progress.
 * Expected progress is linearly interpolated from creation date to target date.
 */
export function computeOnTrack(goal: Goal): boolean {
  const created = parseISO(goal.createdAt);
  const target = parseISO(goal.targetDate);
  if (!isValid(created) || !isValid(target)) return true;

  const totalDays = differenceInDays(target, created);
  if (totalDays <= 0) return goal.progress >= 100;

  const elapsed = differenceInDays(new Date(), created);
  const expectedProgress = Math.min(100, Math.round((elapsed / totalDays) * 100));
  const actualProgress = computeProgressPercentage(goal);

  // Allow 10% tolerance
  return actualProgress >= expectedProgress - 10;
}

/**
 * Compute a confidence score (0-100) based on multiple factors:
 * - progress vs expected timeline
 * - milestone completion rate
 * - recency of updates
 */
export function computeConfidenceScore(goal: Goal): number {
  let score = 50; // baseline

  // Factor 1: Progress vs timeline (+/- 20)
  const onTrack = computeOnTrack(goal);
  score += onTrack ? 20 : -20;

  // Factor 2: Milestone completion momentum (+/- 15)
  if (goal.milestones.length > 0) {
    const completedRatio =
      goal.milestones.filter((m) => m.completed).length / goal.milestones.length;
    score += Math.round(completedRatio * 15);
  }

  // Factor 3: Recency of update (+/- 15)
  const updated = parseISO(goal.updatedAt);
  if (isValid(updated)) {
    const daysSinceUpdate = differenceInDays(new Date(), updated);
    if (daysSinceUpdate <= 7) score += 15;
    else if (daysSinceUpdate <= 14) score += 5;
    else if (daysSinceUpdate > 30) score -= 10;
  }

  return Math.min(100, Math.max(0, score));
}

/**
 * Compute all fields at once.
 */
export function computeGoalFields(goal: Goal): GoalComputedFields {
  return {
    progressPercentage: computeProgressPercentage(goal),
    daysRemaining: computeDaysRemaining(goal),
    onTrack: computeOnTrack(goal),
    confidenceScore: computeConfidenceScore(goal),
  };
}

/**
 * A-022: Auto-generate 3-5 milestones based on goal type.
 */
export function generateMilestonesForType(
  goalType: GoalType,
  title: string
): string[] {
  const milestoneMap: Record<GoalType, string[]> = {
    'career-advancement': [
      'Research target roles and requirements',
      'Update resume and LinkedIn profile',
      'Build relationships with key stakeholders',
      'Apply for or pitch for the advancement',
      'Negotiate terms and transition',
    ],
    'skill-acquisition': [
      'Define specific skill targets and resources',
      'Complete foundational learning materials',
      'Build a practice project or portfolio piece',
      'Get feedback from peers or mentors',
      'Apply skill in a real-world scenario',
    ],
    'financial-target': [
      'Calculate current baseline and gap',
      'Create a monthly savings/investment plan',
      'Set up automatic contributions',
      'Review progress and adjust strategy',
      'Reach target amount',
    ],
    certification: [
      'Register for the certification exam',
      'Complete required study materials',
      'Take practice exams and identify gaps',
      'Schedule and pass the exam',
    ],
    networking: [
      'Identify 10 key contacts to build relationships with',
      'Attend 3 industry events or meetups',
      'Schedule informational interviews',
      'Follow up and nurture connections',
    ],
    'side-business': [
      'Validate the business idea with potential customers',
      'Create a minimal viable product or service offering',
      'Make first sale or acquire first client',
      'Establish repeatable processes',
      'Reach initial revenue milestone',
    ],
    education: [
      'Research programs and admission requirements',
      'Complete and submit applications',
      'Secure funding or financial aid',
      'Begin coursework',
    ],
    relocation: [
      'Research target locations and cost of living',
      'Secure employment or remote work approval',
      'Plan logistics (housing, moving, etc.)',
      'Execute the move',
    ],
    'work-life-balance': [
      'Audit current time allocation',
      'Identify boundaries to set with work',
      'Establish a daily routine that supports balance',
      'Build accountability for personal time',
    ],
    custom: [
      `Define success criteria for "${title}"`,
      'Break down into actionable steps',
      'Complete first major milestone',
      'Review and adjust approach',
    ],
  };

  return milestoneMap[goalType] || milestoneMap.custom;
}

/**
 * A-023: Financial target metrics for financial goals.
 */
export interface FinancialTargetMetrics {
  targetAmount: number;
  currentAmount: number;
  monthlyContributionNeeded: number;
  currency: string;
}

export function computeFinancialMetrics(
  goal: Goal,
  currentAmount = 0
): FinancialTargetMetrics | null {
  if (goal.category !== 'financial' && goal.goalType !== 'financial-target')
    return null;

  const targetAmount = goal.costEstimate || 0;
  const daysRemaining = computeDaysRemaining(goal);
  const monthsRemaining = Math.max(1, daysRemaining / 30);
  const gap = Math.max(0, targetAmount - currentAmount);

  return {
    targetAmount,
    currentAmount,
    monthlyContributionNeeded: Math.ceil(gap / monthsRemaining),
    currency: 'USD',
  };
}
